/**
 * Backend persistence for the Revenue Attribution wizard config.
 *
 * The setup is stored opaquely as JSON on ai-visibility-api, scoped to the caller's product
 * (resolved server-side from the token). These calls are ONLY made in non-demo mode — demo mode
 * keeps its localStorage-only behaviour (see setupState.saveSetupState) and never touches the
 * network, per the repo convention.
 */
import { API_ENDPOINTS } from "constants/http-endpoints";
import Cookies from "js-cookie";

import type { PersistedSetup } from "./setupState";

const SETUP_URL = API_ENDPOINTS.REVENUE_ATTRIBUTION_SETUP;

/** Report status mirrored to the backend so a reload can restore the right tab state. */
export type AttributionSetupStatus = "not_configured" | "syncing" | "configured";

export type LoadedAttributionSetup = {
    setup: PersistedSetup | null;
    status?: AttributionSetupStatus;
    updatedAt?: string;
};

function authHeader(): Record<string, string> | null {
    const token = Cookies.get("system_token");
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
}

/**
 * Fetch the product's saved setup. Returns `{ setup: null }` when nothing is stored yet (the
 * empty/NOT_CONFIGURED state) and null when there is no auth token to call with.
 */
export async function getAttributionSetup(): Promise<LoadedAttributionSetup | null> {
    const auth = authHeader();
    if (!auth) return null;
    const res = await fetch(SETUP_URL, { headers: auth });
    if (!res.ok) throw new Error(`getAttributionSetup failed: ${res.status}`);
    return (await res.json()) as LoadedAttributionSetup;
}

/** Upsert the product's setup blob. */
export async function saveAttributionSetup(
    setup: PersistedSetup,
    status: AttributionSetupStatus = "configured"
): Promise<void> {
    const auth = authHeader();
    if (!auth) throw new Error("saveAttributionSetup: no auth token");
    const res = await fetch(SETUP_URL, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth },
        body: JSON.stringify({ setup, status })
    });
    if (!res.ok) throw new Error(`saveAttributionSetup failed: ${res.status}`);
}

/** Remove the product's saved setup (disconnect). */
export async function deleteAttributionSetup(): Promise<void> {
    const auth = authHeader();
    if (!auth) throw new Error("deleteAttributionSetup: no auth token");
    const res = await fetch(SETUP_URL, { method: "DELETE", headers: auth });
    if (!res.ok) throw new Error(`deleteAttributionSetup failed: ${res.status}`);
}
